import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Toaster } from "./components/ui/sonner";

// Pages
import LoginPage from "./pages/LoginPage";
import AdminDashboard from "./pages/AdminDashboard";
import TechnicianDashboard from "./pages/TechnicianDashboard";
import ClientLocatePage from "./pages/ClientLocatePage";
import SettingsPage from "./pages/SettingsPage";
import ReportsPage from "./pages/ReportsPage";
import InvoiceRequestPage from "./pages/InvoiceRequestPage";
import ShortRedirectPage from "./pages/ShortRedirectPage";

// Protected Route Component
function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-reco-500"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Redirect technician to their dashboard
    if (user.role === 'technician') {
      return <Navigate to="/technician" replace />;
    }
    return <Navigate to="/" replace />;
  }

  return children;
}

function AppRoutes() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-reco-500"></div>
      </div>
    );
  }

  const isAdminOrOperateur = user && ['admin', 'operateur'].includes(user.role);

  return (
    <Routes>
      {/* Public routes */}
      <Route 
        path="/login" 
        element={user ? <Navigate to={user.role === 'technician' ? '/technician' : '/'} replace /> : <LoginPage />} 
      />
      <Route path="/locate/:token" element={<ClientLocatePage />} />
      <Route path="/invoice-request/:token" element={<InvoiceRequestPage />} />
      <Route path="/r/:code" element={<ShortRedirectPage />} />

      {/* Root: Admin dashboard if admin/operateur, Login page otherwise (NEVER redirect to /technician) */}
      <Route
        path="/"
        element={isAdminOrOperateur ? <AdminDashboard /> : <LoginPage />}
      />
      
      {/* Admin only routes */}
      <Route
        path="/settings"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <SettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <ReportsPage />
          </ProtectedRoute>
        }
      />

      {/* Technician routes */}
      <Route
        path="/technician"
        element={
          <ProtectedRoute allowedRoles={['technician', 'admin']}>
            <TechnicianDashboard />
          </ProtectedRoute>
        }
      />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
